// The site as visitors will see it, with live data, rendered by the backend
// from the editor's current (unsaved) state. It runs in a sandboxed iframe
// with no access to the portal; the site script inside reports clicks and
// text edits with postMessage.
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import { API_BASE_URL } from "../../store/baseQuery";

const WIDTHS = { desktop: "100%", tablet: "768px", mobile: "390px" };

export const fetchPreviewHtml = async (token, site, editMode) => {
  const res = await fetch(`${API_BASE_URL}/api/ai/site/preview`, {
    method: "POST",
    headers: { Authorization: token, "Content-Type": "application/json" },
    body: JSON.stringify({ site, editMode }),
  });
  const text = await res.text();
  if (!res.ok) {
    let message = "The preview couldn't be shown.";
    try { message = JSON.parse(text).error || message; } catch (e) { /* HTML error page */ }
    throw new Error(message);
  }
  return text;
};

const SitePreview = ({ site, version, device, selected, focusNonce, onText, onImage, onLive, onSelect }) => {
  const token = useSelector((state) => state.Auth?.user?.accessToken);
  const frameRef = useRef(null);
  const siteRef = useRef(site);
  siteRef.current = site;
  const scrollY = useRef(0);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const handlers = useRef({});
  handlers.current = { onText, onImage, onLive, onSelect };

  const [html, setHtml] = useState(null);
  const renders = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Re-render when the parent says the page changed (not on inline text
  // edits: the page already shows those). Bursts of changes wait for a pause.
  useEffect(() => {
    if (!siteRef.current || !token) return undefined;
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const page = await fetchPreviewHtml(token, siteRef.current, true);
        // Typing on the page changes the frame but not this string, so an undo
        // can bring back the very same HTML. Make each render unique so the
        // frame always reloads.
        renders.current += 1;
        if (!cancelled) { setHtml(`${page}<!-- ${renders.current} -->`); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [version, token]);

  const post = (msg) => frameRef.current?.contentWindow?.postMessage(msg, "*");

  useEffect(() => {
    const onMessage = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      const msg = e.data || {};
      const h = handlers.current;
      if (msg.type === "mh:ready") {
        post({ type: "mh:scroll", y: scrollY.current });
        post({ type: "mh:highlight", section: selectedRef.current });
      } else if (msg.type === "mh:scrolled") scrollY.current = msg.y;
      else if (msg.type === "mh:text") h.onText?.(String(msg.section), String(msg.id), String(msg.text ?? ""));
      else if (msg.type === "mh:image") h.onImage?.(String(msg.section), String(msg.id), String(msg.src || ""));
      else if (msg.type === "mh:live") h.onLive?.(String(msg.source));
      else if (msg.type === "mh:select") h.onSelect?.(String(msg.section));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Selecting a section in the panel scrolls the page to it.
  useEffect(() => {
    post({ type: "mh:highlight", section: selected, scroll: focusNonce > 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, focusNonce]);

  return (
    <div className="relative flex h-full justify-center bg-gray-100 p-0 sm:p-4">
      <div className="relative h-full overflow-hidden bg-white shadow-sm transition-all sm:rounded-lg" style={{ width: WIDTHS[device] || "100%", maxWidth: "100%" }}>
        {html && (
          <iframe
            ref={frameRef}
            title="Website preview"
            srcDoc={html}
            sandbox="allow-scripts"
            className="h-full w-full border-0"
          />
        )}
        {(loading || !html) && !error && (
          <div className={`absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs text-gray-600 shadow ${!html ? "left-1/2 right-auto top-1/2 -translate-x-1/2 -translate-y-1/2" : ""}`}>
            <Loader2 size={14} className="animate-spin" /> Updating preview…
          </div>
        )}
        {error && (
          <div role="alert" className="absolute inset-x-4 top-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default SitePreview;
